import React, { SetStateAction, useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useSearchParams } from 'react-router-dom';

import { ODSemenItem } from '@/services/semen-pull';

import {
  useODSemenQuery,
  useODSimilarSemenQuery,
} from '../../../services/semen-pull/hooks';
import { useSemenPullContext } from '../useSemenPullContext';
import { PullODSemenDialog } from './PullODSemenDialog';

type Props = {
  openODSemenDialogVisible: boolean;
  setOpenODSemenDialogVisible: React.Dispatch<SetStateAction<boolean>>;
};

type SearchParams = {
  fertSiteId: string;
  semenName: string;
  accountName: string;
  caneCode: string;
  colCode: string;
};

export const OpenODSemenDialog = ({
  openODSemenDialogVisible,
  setOpenODSemenDialogVisible,
}: Props) => {
  const { setODselectedSemen } = useSemenPullContext();
  const [params] = useSearchParams();

  const selectedSiteId = params.get('site');
  const [selectSemenVisible, setSelectSemenVisible] = useState(false);

  const [inputs, setInputs] = useState({
    semenInput: '',
    accountInput: '',
    caneInput: '',
    colCodeInput: '',
  });

  const [submittedParams, setSubmittedParams] = useState<SearchParams | null>(
    null,
  );
  const [searchMode, setSearchMode] = useState<'exact' | 'similar' | null>(
    null,
  );

  const exactQuery = useODSemenQuery(
    submittedParams,
    searchMode === 'exact' && !!submittedParams,
  );
  const similarQuery = useODSimilarSemenQuery(
    submittedParams,
    searchMode === 'similar' && !!submittedParams,
  );

  const odSemen =
    searchMode === 'exact'
      ? exactQuery.data
      : searchMode === 'similar'
        ? similarQuery.data
        : undefined;

  const buildParams = (): SearchParams => ({
    fertSiteId: selectedSiteId as string,
    semenName: inputs.semenInput,
    accountName: inputs.accountInput,
    caneCode: inputs.caneInput,
    colCode: inputs.colCodeInput,
  });

  const resetODSemenDialog = () => {
    setSubmittedParams(null);
    setSearchMode(null);
    setInputs({
      semenInput: '',
      accountInput: '',
      caneInput: '',
      colCodeInput: '',
    });
    setOpenODSemenDialogVisible(false);
  };

  return (
    <Dialog
      header="Open OD Semen"
      visible={openODSemenDialogVisible}
      style={{ width: '80vw' }}
      onHide={() => {
        if (!openODSemenDialogVisible) return;
        setSubmittedParams(null);
        setSearchMode(null);
        setInputs({
          semenInput: '',
          accountInput: '',
          caneInput: '',
          colCodeInput: '',
        });
        setODselectedSemen(undefined);
        setOpenODSemenDialogVisible(false);
      }}
      className="[&_.p-dialog-content]:flex [&_.p-dialog-content]:flex-col [&_.p-dialog-content]:gap-4"
      maximizable
    >
      <div className="mb-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="">Semen:</label>
          <InputText
            value={inputs.semenInput}
            onChange={(e) =>
              setInputs({ ...inputs, semenInput: e.target.value })
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="">Account:</label>
          <InputText
            value={inputs.accountInput}
            onChange={(e) =>
              setInputs({ ...inputs, accountInput: e.target.value })
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="">Cane:</label>
          <InputText
            value={inputs.caneInput}
            onChange={(e) =>
              setInputs({ ...inputs, caneInput: e.target.value })
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="">Col. Code:</label>
          <InputText
            value={inputs.colCodeInput}
            onChange={(e) =>
              setInputs({ ...inputs, colCodeInput: e.target.value })
            }
          />
        </div>
      </div>
      <div className="flex justify-center gap-4">
        <Button
          label="Search Exact Semen"
          severity="success"
          loading={exactQuery.isFetching}
          onClick={() => {
            setSubmittedParams(buildParams());
            setSearchMode('exact');
          }}
        />
        <Button
          label="Search Similar Semen"
          severity="warning"
          loading={similarQuery.isFetching}
          onClick={() => {
            setSubmittedParams(buildParams());
            setSearchMode('similar');
          }}
        />
      </div>
      <div className="w-full">
        <DataTable
          scrollable
          scrollHeight="flex"
          value={odSemen as ODSemenItem[]}
          tableStyle={{ minWidth: '50rem', maxHeight: '.5rem' }}
        >
          <Column field="semenName" header="SemenName"></Column>
          <Column field="semenTypeName" header="Semen Type"></Column>
          <Column field="accountName" header="Ranch"></Column>
          <Column field="cane" header="Cane"></Column>
          <Column field="colCode" header="Col Code"></Column>
          <Column field="semenStorageName" header="Storage Location"></Column>
          <Column field="quantity" header="Available"></Column>
          <Column
            header="Select"
            body={(row) => {
              return (
                <Button
                  severity="success"
                  onClick={() => {
                    setODselectedSemen(row);
                    setSelectSemenVisible(true);
                  }}
                >
                  Select Semen
                </Button>
              );
            }}
          ></Column>
        </DataTable>
        <PullODSemenDialog
          selectSemenVisible={selectSemenVisible}
          setSelectSemenVisible={setSelectSemenVisible}
          resetODSemenDialog={resetODSemenDialog}
        />
      </div>
    </Dialog>
  );
};
